// One-shot interest-resume trigger, shared by the native and Almanac event
// detail pages. When an anonymous viewer clicks an interest control they are
// sent to `/login?redirect_to=<detail path with ?interest=...>`; on return the
// marker rides inside the URL. Once auth/enrichment has settled AND the control
// is a live toggle (see each page's `ready`), this replays the intended rung
// exactly once, then strips the marker so a refresh/back-nav doesn't re-fire it.
//
// Marker → rung mapping (mirrors the `EventsEventInterestControl` login URLs):
//   '1'          → 'interested'  (native single toggle)
//   'interested' → 'interested'  (Almanac)
//   'going'      → 'going'       (Almanac)
// Any other value is ignored for resume but still stripped from the URL.
//
// Client-only: driven by client-side enrichment and a history replace — both
// no-ops during SSR.

import { ref, watch } from 'vue';
import type { Ref } from 'vue';
import type { InterestRung } from '@@/types/api';

interface UseInterestResumeOptions {
  // Fire only once auth/enrichment has settled AND the control is a live toggle.
  ready: Ref<boolean>;
  // The page provides the action (calls setInterest). May be async.
  resume: (rung: InterestRung) => Promise<void> | void;
}

export function useInterestResume(opts: UseInterestResumeOptions): void {
  const { ready, resume } = opts;

  if (!import.meta.client) return;

  const route = useRoute();
  const router = useRouter();

  // One-shot guard: the resume (or the marker cleanup) fires at most once per
  // page mount.
  const hasResumed = ref(false);

  function clearInterestParam() {
    const rest = { ...route.query };
    delete rest.interest;
    router.replace({ query: rest });
  }

  function markerToRung(value: unknown): InterestRung | null {
    if (value === '1' || value === 'interested') return 'interested';
    if (value === 'going') return 'going';
    return null;
  }

  watch(
    [() => route.query.interest, ready],
    () => {
      if (hasResumed.value) return;
      const marker = route.query.interest;
      // No marker → nothing to resume.
      if (marker == null) return;
      // Wait for auth/enrichment to settle and the control to become a live
      // toggle before replaying the intent.
      if (!ready.value) return;

      hasResumed.value = true;

      const rung = markerToRung(marker);
      if (rung) {
        // Never throw into render: the action composable surfaces its own
        // snackbar on failure, so a rejection here is safe to swallow.
        void Promise.resolve(resume(rung)).catch(() => {});
      }
      // Strip the marker whether or not it mapped to a rung, so a refresh
      // doesn't re-fire and the URL stays clean.
      clearInterestParam();
    },
    { immediate: true },
  );
}
